import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security,
  VpnKey,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useAdminAuth } from '../context/AdminAuthContext';

const AdminLogin: React.FC = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    mfaCode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSubmitting, setForgotPasswordSubmitting] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  
  const { login, error, clearError, isLoading } = useAdminAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    clearError();

    try {
      const result = await login(credentials.email, credentials.password, credentials.mfaCode);
      if (result.requiresMfa) {
        setRequiresMfa(true);
      }
    } catch (error) {
      // Error is handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof credentials) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCredentials(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (error) clearError();
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordSubmitting(true);
    setForgotPasswordMessage('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: forgotPasswordEmail,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setForgotPasswordMessage(data.message);
      } else {
        setForgotPasswordMessage(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      setForgotPasswordMessage('Failed to send reset email. Please try again.');
    } finally {
      setForgotPasswordSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#000"
        color="white"
      >
        <Box textAlign="center">
          <CircularProgress color="primary" size={40} sx={{ mb: 2 }} />
          <Typography variant="h6">🐾 Pawtel Admin Portal</Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            Validating session...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <AdminPanelSettings 
              sx={{ fontSize: 48, color: '#000', mb: 1 }} 
            />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🐾 Pawtel Admin Portal
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Authorized Personnel Only
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Admin Email"
              type="email"
              value={credentials.email}
              onChange={handleInputChange('email')}
              margin="normal"
              required
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AdminPanelSettings color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={handleInputChange('password')}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKey color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* MFA Code - only show when required or already entered */}
            {(requiresMfa || credentials.mfaCode) && (
              <TextField
                fullWidth
                label="MFA Code (6 digits)"
                value={credentials.mfaCode}
                onChange={handleInputChange('mfaCode')}
                margin="normal"
                required={requiresMfa}
                inputProps={{ 
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2em' }
                }}
                placeholder="000000"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Security color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText={requiresMfa 
                  ? "Enter the 6-digit code from your authenticator app or use a backup code" 
                  : "Optional: Enter MFA code if you have it enabled"
                }
              />
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting || !credentials.email || !credentials.password || (requiresMfa && !credentials.mfaCode)}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #000000 30%, #333333 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #333333 30%, #555555 90%)',
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Secure Login'
              )}
            </Button>
          </form>

          {/* Forgot Password Link */}
          <Box textAlign="center" mt={2}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowForgotPassword(!showForgotPassword)}
              sx={{ textDecoration: 'none', color: 'primary.main' }}
            >
              Forgot Password?
            </Link>
          </Box>

          {/* Forgot Password Form */}
          {showForgotPassword && (
            <Box mt={3} p={2} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              
              {forgotPasswordMessage && (
                <Alert 
                  severity={forgotPasswordMessage.includes('error') || forgotPasswordMessage.includes('Failed') ? 'error' : 'success'} 
                  sx={{ mb: 2 }}
                >
                  {forgotPasswordMessage}
                </Alert>
              )}

              <form onSubmit={handleForgotPassword}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  margin="normal"
                  required
                  size="small"
                />
                
                <Box display="flex" gap={1} mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={forgotPasswordSubmitting || !forgotPasswordEmail}
                    size="small"
                  >
                    {forgotPasswordSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordMessage('');
                      setForgotPasswordEmail('');
                    }}
                    size="small"
                  >
                    Cancel
                  </Button>
                </Box>
              </form>
            </Box>
          )}

          {/* Security Notice */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="body2" color="text.secondary" textAlign="center">
              🔒 This portal is protected by IP restrictions, VPN requirements, and multi-factor authentication.
              <br />
              All access attempts are logged and monitored.
            </Typography>
          </Box>


        </Paper>
      </Container>
    </Box>
  );
};

export default AdminLogin;